import { TranslationKeys } from 'Constants/TranslationKeys'
import { isProdSite, renderTranslation } from 'Helpers/GenericHelpers'
import { Col, Popover, Row } from 'antd'

import { useAppSelector } from 'hooks'
import { SettingOutlined } from '@ant-design/icons'
import { useNavigateWrapper } from 'Helpers/NavigateHelper'
import { useMemo } from 'react'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import {
  retrieveChannelMatchWithPricelist,
  retrieveSiteMatchWithPriceList,
  retrieveStoresMatchWithPricelist,
} from 'Helpers/DiscountHelper'
import { getPricelistName } from 'Helpers/PricesHelper'

export type ActiveListingPopoverProps = {
  activeListing?: any
  basePrice?: any
  customListToShowByType?: any
  channelType: any
  showPopoverContent: boolean
  showSettingView?: any
  id: any
  additionalContent?: any
  openAction?: any
  onOpenChangeAction?: any
  getPopupContainer?: any
  popoverTriggerContent: any
}

export default function ActiveListingPopover(props: ActiveListingPopoverProps) {
  const channelList = useAppSelector(state => state.present.channel.channelList)
  const listings = useAppSelector(state => state.present.configuration.allPricelists)
  const language = useAppSelector(state => state.present.language.value)

  const pricelists = useAppSelector(state => state.present.site.pricelists)
  const stores = useAppSelector(state => state.present.store.stores)
  const listingInformation = listings.find(x => x.name === props.activeListing)
  const isListingTypeSell = (listingInformation && listingInformation.isSell) || !listingInformation

  const listingType = isListingTypeSell
    ? renderTranslation(language, TranslationKeys.listingPopListingSell)
    : renderTranslation(language, TranslationKeys.listingPopListingBuy)

  const listingB2C =
    listingInformation && listingInformation.b2c
      ? renderTranslation(language, TranslationKeys.listingPopListingB2C)
      : ''
  const listingSize =
    listingInformation && listingInformation.isBySize
      ? renderTranslation(language, TranslationKeys.tableMktYes)
      : renderTranslation(language, TranslationKeys.tableMktNo)

  const listingVat =
    listingInformation && listingInformation.ltVat
      ? renderTranslation(language, TranslationKeys.listingPopListingVatYes)
      : renderTranslation(language, TranslationKeys.listingPopListingVatNo)

  const listHeaderTitle = useMemo(() => {
    switch (props.channelType) {
      case TypeOfChannel.Marketplace:
        return renderTranslation(language, TranslationKeys.listingPopListingTitle)
      case TypeOfChannel.Site:
        return renderTranslation(language, TranslationKeys.listingPopListingTitleSite)
      case TypeOfChannel.Store:
        return renderTranslation(language, TranslationKeys.listingPopListingTitleStore)
      default:
        return ''
    }
  }, [language, props.channelType])

  const listToShowByType = useMemo(() => {
    switch (props.channelType) {
      case TypeOfChannel.Marketplace:
        const channelBaseOnPricelist = retrieveChannelMatchWithPricelist(props.activeListing, channelList)
        return channelBaseOnPricelist.map(x => {
          return <div className='pop-list exclude-modal-trigger'>{x.name}</div>
        })
      case TypeOfChannel.Site:
        const siteBasedOnPricelist = retrieveSiteMatchWithPriceList(props.activeListing, pricelists)
        return siteBasedOnPricelist.map(x => {
          return <div className='pop-list exclude-modal-trigger'>{x}</div>
        })
      case TypeOfChannel.Store:
        const storesBasedOnPricelist = retrieveStoresMatchWithPricelist(props.activeListing, stores)
        return storesBasedOnPricelist.map(x => {
          return <div className='pop-list exclude-modal-trigger'>{x}</div>
        })
      default:
        return []
    }
  }, [channelList, pricelists, props.activeListing, stores, props.channelType])

  const { navigateToChannels } = useNavigateWrapper()

  return (
    <div>
      <Popover
        title={
          <div className='exclude-modal-trigger'>
            {renderTranslation(language, TranslationKeys.listingPopTitle) +
              ' ' +
              getPricelistName(props.activeListing, language)}
          </div>
        }
        align={{ offset: [0, 30] }}
        content={
          <Row className={'pop-channel-info-box exclude-modal-trigger'}>
            {props.showPopoverContent ? (
              <>
                <Col span={12} className='pop-box-left exclude-modal-trigger'>
                  {!isProdSite() ? (
                    <SettingOutlined
                      className='exclude-modal-trigger float-right'
                      onClick={props.showSettingView ? props.showSettingView : navigateToChannels}
                    />
                  ) : null}
                  <div className='pop-list-title pop-list exclude-modal-trigger'>{listHeaderTitle}</div>
                  {props.customListToShowByType ? props.customListToShowByType : listToShowByType}
                </Col>

                <Col span={12} className='right-info exclude-modal-trigger'>
                  <div className='exclude-modal-trigger'>
                    <span className='pre-list-text exclude-modal-trigger'>
                      {renderTranslation(language, TranslationKeys.listingPopListingType) + ':'}
                    </span>
                    {listingType}
                  </div>
                  <div className='exclude-modal-trigger'>
                    <span className='exclude-modal-trigger'>{listingB2C}</span>
                  </div>
                  <div className='exclude-modal-trigger'>
                    <span className='pre-list-text exclude-modal-trigger'>
                      {renderTranslation(language, TranslationKeys.listingPopListingVat) + ':'}
                    </span>
                    {listingVat}
                  </div>
                  <div className='exclude-modal-trigger'>
                    <span className='pre-list-text exclude-modal-trigger'>
                      {renderTranslation(language, TranslationKeys.scattoTaglia) + ':'}
                    </span>
                    {listingSize}
                  </div>
                </Col>
              </>
            ) : null}

            {props.additionalContent}
          </Row>
        }
        className={'active-listing-popover exclude-modal-trigger'}
        id={props.id}
        trigger='click'
        placement='bottom'
        open={props.openAction}
        onOpenChange={props.onOpenChangeAction}
        getPopupContainer={props.getPopupContainer}
      >
        {props.popoverTriggerContent}
      </Popover>
    </div>
  )
}
